import { useState, useCallback, useEffect, useRef } from 'react';
import type { KeyboardEvent } from 'react';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  startOfMonth,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
  format,
  isToday as isTodayFn,
  isPast as isPastFn,
  startOfDay,
  getDaysInMonth,
  getDay,
  isBefore,
  isAfter,
  addDays,
  addYears,
  startOfWeek,
  endOfWeek,
} from 'date-fns';
import { cn } from '@clarix/ui';
import { Button } from '@clarix/ui';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@clarix/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@clarix/ui';

// ── Shared Types ────────────────────────────────────────────────────

interface CalendarGridProps {
  value: Date | null;
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  /** When true, past dates are styled as selectable (e.g. for analytics). Default false = past styled muted */
  allowPast?: boolean;
  /**
   * Caption style. `'label'` (default) renders the static "July 2026" caption;
   * `'dropdown'` renders month + year selects so far-away dates (a date of
   * birth, a backdated joining date) are reachable without stepping month by
   * month through the chevrons.
   */
  captionLayout?: 'label' | 'dropdown';
  /** Optional dot under a day; `className` colours it, `label` is appended to the day's aria-label. */
  getDayIndicator?: (date: Date) => DayIndicator | null;
}

export interface DayIndicator {
  className: string;
  label: string;
}

// ── CaptionDropdowns (internal) ─────────────────────────────────────

const CAPTION_MONTH_LABELS = Array.from({ length: 12 }, (_, i) =>
  format(new Date(2000, i, 1), 'MMMM'),
);

function CaptionDropdowns({
  viewMonth,
  onViewMonthChange,
  minDate,
  maxDate,
}: {
  viewMonth: Date;
  onViewMonthChange: (month: Date) => void;
  minDate?: Date;
  maxDate?: Date;
}) {
  const currentYear = new Date().getFullYear();
  // Year bounds follow min/max when given; otherwise wide enough for a date of
  // birth a century back and a future effective date. The view year is always
  // included so a stored out-of-bounds value still displays.
  const fromYear = Math.min(
    minDate?.getFullYear() ?? currentYear - 100,
    viewMonth.getFullYear(),
  );
  const toYear = Math.max(
    maxDate?.getFullYear() ?? currentYear + 10,
    viewMonth.getFullYear(),
  );
  const years = Array.from(
    { length: toYear - fromYear + 1 },
    (_, i) => fromYear + i,
  );

  // Radix Select, not a native <select>: its list portals to <body> AFTER any
  // open Dialog/Sheet/Popover portal, so at the shared z-50 it paints above
  // them, and @clarix/ui's popover ScrollLock exempts listbox/menu layers so
  // the year list stays wheel-scrollable inside the calendar popover.
  const triggerCls =
    'h-auto w-auto gap-0.5 rounded border-0 bg-transparent px-1 py-0.5 text-xl font-semibold text-foreground hover:bg-muted [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground [&>svg]:opacity-100';

  return (
    <div className="flex items-center">
      <Select
        value={String(viewMonth.getMonth())}
        onValueChange={(v) =>
          onViewMonthChange(
            new Date(viewMonth.getFullYear(), Number(v), 1),
          )
        }
      >
        <SelectTrigger aria-label="Month" className={triggerCls}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CAPTION_MONTH_LABELS.map((label, i) => (
            <SelectItem key={label} value={String(i)}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={String(viewMonth.getFullYear())}
        onValueChange={(v) =>
          onViewMonthChange(new Date(Number(v), viewMonth.getMonth(), 1))
        }
      >
        <SelectTrigger aria-label="Year" className={triggerCls}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {years.map((y) => (
            <SelectItem key={y} value={String(y)}>
              {y}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ── CalendarGrid (internal) ─────────────────────────────────────────

const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function CalendarGrid({
  value,
  viewMonth,
  onSelect,
  onPrevMonth,
  onNextMonth,
  onViewMonthChange,
  minDate,
  maxDate,
  allowPast = false,
  captionLayout = 'label',
  getDayIndicator,
}: CalendarGridProps & {
  viewMonth: Date;
  onSelect: (date: Date) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onViewMonthChange: (month: Date) => void;
}) {
  const totalDays = getDaysInMonth(viewMonth);
  const firstDayOfWeek = (getDay(startOfMonth(viewMonth)) + 6) % 7;
  const [focused, setFocused] = useState<Date | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const focusPending = useRef(false);
  const activeDate = rovingDay(focused, value, viewMonth, minDate, maxDate);

  // Runs after every render so a keyboard move that also changed the month
  // focuses the day once the new month's buttons exist.
  useEffect(() => {
    if (!focusPending.current) return;
    focusPending.current = false;
    gridRef.current
      ?.querySelector<HTMLButtonElement>('[data-day][tabindex="0"]')
      ?.focus();
  });

  const handleGridKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!activeDate) return;
    const target = keyTarget(e.key, e.shiftKey, activeDate);
    if (!target) return;
    e.preventDefault();
    const next = clampDate(target, minDate, maxDate);
    setFocused(next);
    if (!isSameMonth(next, viewMonth)) onViewMonthChange(startOfMonth(next));
    focusPending.current = true;
  };

  return (
    <>
      {/* Month navigation */}
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={onPrevMonth}
          aria-label="Previous month"
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
        </button>
        {captionLayout === 'dropdown' ? (
          <CaptionDropdowns
            viewMonth={viewMonth}
            onViewMonthChange={onViewMonthChange}
            minDate={minDate}
            maxDate={maxDate}
          />
        ) : (
          <span className="text-xl font-semibold text-foreground">
            {format(viewMonth, 'MMMM yyyy')}
          </span>
        )}
        <button
          type="button"
          onClick={onNextMonth}
          aria-label="Next month"
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 text-center">
        {WEEKDAY_LABELS.map((d) => (
          <div
            key={d}
            className="pb-1 text-xs font-semibold text-muted-foreground"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Days grid: one tab stop, arrow keys rove between days. */}
      <div
        ref={gridRef}
        onKeyDown={handleGridKeyDown}
        className="grid grid-cols-7 gap-y-0.5"
      >
        {Array.from({ length: firstDayOfWeek }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}

        {Array.from({ length: totalDays }).map((_, i) => {
          const day = i + 1;
          const date = new Date(
            viewMonth.getFullYear(),
            viewMonth.getMonth(),
            day,
          );
          const isSelected = value ? isSameDay(date, value) : false;
          const isCurrentMonth = isSameMonth(date, viewMonth);
          const isToday = isTodayFn(date);
          const isPast = isPastFn(startOfDay(date)) && !isToday;
          const isBeforeMin =
            minDate &&
            isBefore(startOfDay(date), startOfDay(minDate));
          const isAfterMax =
            maxDate &&
            isAfter(startOfDay(date), startOfDay(maxDate));
          const isDisabled =
            !isCurrentMonth || !!isBeforeMin || !!isAfterMax;
          const indicator = getDayIndicator?.(date) ?? null;
          const isActive = activeDate ? isSameDay(date, activeDate) : false;

          return (
            <button
              key={day}
              type="button"
              data-day
              tabIndex={isActive ? 0 : -1}
              onFocus={() => setFocused(date)}
              onClick={() => onSelect(date)}
              disabled={isDisabled}
              aria-label={
                indicator
                  ? `${format(date, 'd MMMM yyyy')}, ${indicator.label}`
                  : undefined
              }
              className={cn(
                'mx-auto flex h-8 w-8 items-center justify-center rounded-full text-lg transition-colors',
                indicator && 'relative',
                isDisabled
                  ? 'cursor-not-allowed text-muted-foreground/30'
                  : isSelected
                    ? 'bg-primary font-semibold text-primary-foreground'
                    : isToday
                      ? 'font-semibold text-primary ring-1 ring-[rgb(var(--tone-brand-border))]'
                      : isPast && !allowPast
                        ? 'text-muted-foreground/40'
                        : 'text-foreground hover:bg-muted',
              )}
            >
              {day}
              {indicator && (
                <span
                  aria-hidden
                  className={cn(
                    'absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full',
                    indicator.className,
                  )}
                />
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}

// ── DatePicker (standalone with Popover) ────────────────────────────

interface DatePickerProps {
  value: Date | null;
  onChange: (date: Date) => void;
  placeholder?: string;
  showRelativeLabels?: boolean;
  minDate?: Date;
  maxDate?: Date;
  /** When true, past dates appear selectable (e.g. for analytics). Default false */
  allowPast?: boolean;
  disabled?: boolean;
  triggerClassName?: string;
  /** date-fns format token for the trigger label. Default: `'EEE, MMM d'`. */
  displayFormat?: string;
  /** Alias for `displayFormat`. */
  format?: string;
  /** Alias for `triggerClassName`. */
  className?: string;
  align?: 'start' | 'center' | 'end';
  /** HTML id forwarded to the trigger Button (for label / `htmlFor` association). */
  id?: string;
  /**
   * `'dropdown'` swaps the static "July 2026" caption for month + year selects,
   * for fields whose value is typically far from today (date of birth, backdated
   * joining date). Default `'label'`.
   */
  captionLayout?: 'label' | 'dropdown';
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Pick date',
  showRelativeLabels = true,
  minDate,
  maxDate,
  allowPast = false,
  disabled = false,
  triggerClassName,
  displayFormat,
  format: formatAlias,
  className,
  align = 'start',
  id,
  captionLayout = 'label',
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const fmt = displayFormat ?? formatAlias ?? 'EEE, MMM d';
  const triggerCls = triggerClassName ?? className;
  const [viewMonth, setViewMonth] = useState(
    () => startOfMonth(value ?? new Date()),
  );

  const contentRef = useRef<HTMLDivElement>(null);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) setViewMonth(startOfMonth(value ?? new Date()));
      setOpen(next);
    },
    [value],
  );

  // Land on the selected day (else today) instead of the "Previous month" chevron.
  const handleOpenAutoFocus = useCallback((e: Event) => {
    const day = contentRef.current?.querySelector<HTMLButtonElement>(
      '[data-day][tabindex="0"]',
    );
    if (!day) return;
    e.preventDefault();
    day.focus();
  }, []);

  const handleSelect = useCallback(
    (date: Date) => {
      onChange(date);
      setOpen(false);
    },
    [onChange],
  );

  const handlePrevMonth = useCallback(() => {
    setViewMonth((m) => subMonths(m, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setViewMonth((m) => addMonths(m, 1));
  }, []);

  const handleViewMonthChange = useCallback((month: Date) => {
    setViewMonth(startOfMonth(month));
  }, []);

  const displayLabel = value
    ? showRelativeLabels && isRelativeLabel(value)
      ? getRelativeLabel(value)
      : format(value, fmt)
    : placeholder;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          id={id}
          variant="outline"
          size="xs"
          disabled={disabled}
          className={cn(
            // 13px keeps the trigger (and its placeholder text) in lockstep with the
            // common `Input` / `Textarea` and `AppSelect`, so a form's date field does
            // not read a size larger than the text field beside it.
            'h-8 gap-1.5 text-lg font-medium',
            // This trigger is a FORM FIELD wearing a Button, so it must hover and
            // focus like the `Input` / `AppSelect` beside it — not like a button.
            // Two rules from the Button fought that: the `outline` variant's
            // `hover:bg-accent hover:text-accent-foreground` floods the whole control
            // with Gray 100, and the base's `focus-visible:ring-2 ring-offset-2`
            // (`ring` = `--primary`) wraps it in a heavy indigo halo. Together they
            // made a date field read as a filled block next to inputs that only shift
            // their border. Matched here to AppSelect's dropdown trigger (border shift
            // plus a tint at 20% alpha that is all but invisible) and the Input's thin
            // 1px focus ring. tailwind-merge drops the Button's rules — same groups,
            // these come later. Same fix MonthYearPicker already carries.
            'hover:border-indigo-200 hover:bg-indigo-50/20',
            'focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-ring/40 focus-visible:border-ring/60',
            // Hover text colour is pinned PER STATE rather than left to the variant:
            // `hover:text-accent-foreground` (Gray 700) would darken an unset
            // placeholder on hover, so an empty field appeared to change value on
            // mouseover.
            value ? 'hover:text-foreground' : 'text-muted-foreground hover:text-muted-foreground',
            triggerClassName,
            !value && 'text-muted-foreground',
            triggerCls,
          )}
        >
          <CalendarDays className="h-3.5 w-3.5" strokeWidth={1.5} />
          {displayLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        onOpenAutoFocus={handleOpenAutoFocus}
        className="w-[260px] p-3"
        align={align}
        sideOffset={8}
        collisionPadding={16}
      >
        <CalendarGrid
          value={value}
          viewMonth={viewMonth}
          onSelect={handleSelect}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
          onViewMonthChange={handleViewMonthChange}
          minDate={minDate}
          maxDate={maxDate}
          allowPast={allowPast}
          captionLayout={captionLayout}
          onChange={onChange}
        />
      </PopoverContent>
    </Popover>
  );
}

// ── DatePickerPanel (bare calendar for embedding in existing popovers) ──

interface DatePickerPanelProps {
  value: Date | null;
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  /** When true, past dates appear selectable (e.g. for analytics). Default false */
  allowPast?: boolean;
  className?: string;
  /** `'dropdown'` renders month + year selects in the caption. Default `'label'`. */
  captionLayout?: 'label' | 'dropdown';
  /** Optional per-day status dot (see `DayIndicator`). */
  getDayIndicator?: (date: Date) => DayIndicator | null;
  /** Called with the first of the visible month on mount and on every month change. */
  onViewMonthChange?: (month: Date) => void;
}

export function DatePickerPanel({
  value,
  onChange,
  minDate,
  maxDate,
  allowPast = false,
  className,
  captionLayout = 'label',
  getDayIndicator,
  onViewMonthChange,
}: DatePickerPanelProps) {
  const [viewMonth, setViewMonth] = useState(
    () => startOfMonth(value ?? new Date()),
  );

  useEffect(() => {
    onViewMonthChange?.(viewMonth);
  }, [viewMonth, onViewMonthChange]);

  const handlePrevMonth = useCallback(() => {
    setViewMonth((m) => subMonths(m, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setViewMonth((m) => addMonths(m, 1));
  }, []);

  const handleViewMonthChange = useCallback((month: Date) => {
    setViewMonth(startOfMonth(month));
  }, []);

  return (
    <div className={cn('w-[240px]', className)}>
      <CalendarGrid
        value={value}
        viewMonth={viewMonth}
        onSelect={onChange}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onViewMonthChange={handleViewMonthChange}
        minDate={minDate}
        maxDate={maxDate}
        allowPast={allowPast}
        captionLayout={captionLayout}
        getDayIndicator={getDayIndicator}
        onChange={onChange}
      />
    </div>
  );
}

// ── DatePickerInput (string-based, drop-in <Input type="date"> replacement) ──

interface DatePickerInputProps {
  /** Date value in YYYY-MM-DD format */
  value: string;
  /** Called with YYYY-MM-DD string (or empty string if cleared) */
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  /** When true, past dates appear selectable (e.g. for analytics). Default false */
  allowPast?: boolean;
  /** Popover alignment */
  align?: 'start' | 'center' | 'end';
  /** HTML id forwarded to the trigger Button (for label / `htmlFor` association). */
  id?: string;
  /**
   * `'dropdown'` swaps the static "July 2026" caption for month + year selects,
   * for fields whose value is typically far from today (date of birth, backdated
   * joining date). Default `'label'`.
   */
  captionLayout?: 'label' | 'dropdown';
}

export function DatePickerInput({
  value,
  onChange,
  placeholder = 'Select date',
  disabled = false,
  className,
  minDate,
  maxDate,
  allowPast = false,
  align = 'start',
  id,
  captionLayout = 'label',
}: DatePickerInputProps) {
  const dateValue = value ? parseYMD(value) : null;

  const handleChange = useCallback(
    (date: Date) => {
      onChange(format(date, 'yyyy-MM-dd'));
    },
    [onChange],
  );

  return (
    <DatePicker
      value={dateValue}
      onChange={handleChange}
      placeholder={placeholder}
      showRelativeLabels={false}
      displayFormat="MMM d, yyyy"
      disabled={disabled}
      triggerClassName={className}
      minDate={minDate}
      maxDate={maxDate}
      allowPast={allowPast}
      align={align}
      id={id}
      captionLayout={captionLayout}
    />
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

function clampDate(date: Date, minDate?: Date, maxDate?: Date): Date {
  if (minDate && isBefore(startOfDay(date), startOfDay(minDate))) return startOfDay(minDate);
  if (maxDate && isAfter(startOfDay(date), startOfDay(maxDate))) return startOfDay(maxDate);
  return date;
}

/** The day holding the grid's single tab stop for the visible month, or null when none is selectable. */
function rovingDay(
  focused: Date | null,
  value: Date | null,
  viewMonth: Date,
  minDate?: Date,
  maxDate?: Date,
): Date | null {
  const today = startOfDay(new Date());
  const preferred = [focused, value, today].find(
    (d): d is Date => !!d && isSameMonth(d, viewMonth),
  );
  const day = clampDate(preferred ?? startOfMonth(viewMonth), minDate, maxDate);
  return isSameMonth(day, viewMonth) ? day : null;
}

/** Calendar keyboard model (WAI-ARIA date picker dialog); weeks start on Monday. */
function keyTarget(key: string, shift: boolean, from: Date): Date | null {
  switch (key) {
    case 'ArrowLeft':
      return addDays(from, -1);
    case 'ArrowRight':
      return addDays(from, 1);
    case 'ArrowUp':
      return addDays(from, -7);
    case 'ArrowDown':
      return addDays(from, 7);
    case 'Home':
      return startOfWeek(from, { weekStartsOn: 1 });
    case 'End':
      return startOfDay(endOfWeek(from, { weekStartsOn: 1 }));
    case 'PageUp':
      return shift ? addYears(from, -1) : addMonths(from, -1);
    case 'PageDown':
      return shift ? addYears(from, 1) : addMonths(from, 1);
    default:
      return null;
  }
}

/** Parse YYYY-MM-DD string to a local Date (avoids timezone issues) */
function parseYMD(dateStr: string): Date | null {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function isRelativeLabel(date: Date): boolean {
  const today = startOfDay(new Date());
  const target = startOfDay(date);
  const diffMs = target.getTime() - today.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays === 0 || diffDays === 1;
}

function getRelativeLabel(date: Date): string {
  const today = startOfDay(new Date());
  const target = startOfDay(date);
  const diffMs = target.getTime() - today.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  return format(date, 'EEE, MMM d');
}
